// src/components/Directory/DBHouseTable.jsx

import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './DirectoryTable.css';
import API_BASE_URL from '../../config';


const declOfNum = (n, titles) => {
  const num = Math.abs(n) % 100;
  const n1 = num % 10;
  if (num > 10 && num < 20) return titles[2];
  if (n1 > 1 && n1 < 5) return titles[1];
  if (n1 === 1) return titles[0];
  return titles[2];
};

// Ключи в sessionStorage для сохранения состояния
const STORAGE_SORT_KEY = 'DBHouseTableSort';
const STORAGE_SCROLL_KEY = 'DBHouseTableScroll';

// Ключ в localStorage, где храним список удалённых домов с метками времени
const STORAGE_HIDE_KEY = 'deletedHouses';
const ONE_HOUR = 60 * 60 * 1000;
const STORAGE_ADDED_KEY = 'addedHouses';

const DBHouseTable = ({ query, selectedHouseId, onRowSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Основной список домов (каждый объект должен содержать поле id: number)
  const [houses, setHouses] = useState([]);
  // Список id домов, которые нужно скрывать (оптимистично) — читается из localStorage
  const [hideIds, setHideIds] = useState([]);

  // Сортировка: инициализируем из sessionStorage, если есть
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_SORT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          key: parsed.key,
          direction: parsed.direction || 'asc',
        };
      }
    } catch {}
    return { key: null, direction: 'asc' };
  });

  // Ссылка на контейнер с прокруткой таблицы
  const tableWrapperRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────────────────
  // 1) При монтировании компонента:
  //    - Загружаем список домов и series_name
  //    - Считываем из localStorage ранее удалённые дома, фильтруем устаревшие (>1 час)
  useEffect(() => {
    console.log('[DBHouseTable] Инициализация: загрузка списка домов и очистка устаревших скрытий');
    // 1.a. Загрузка данных
    Promise.all([
      fetch(`${API_BASE_URL}/building/series_all`).then(res => res.json()),
      fetch(`${API_BASE_URL}/building/get_all_build`).then(res => res.json()),
    ])
      .then(([seriesList, houseList]) => {
        const seriesDict = seriesList.reduce((acc, s) => {
          acc[s.id] = s.name;
          return acc;
        }, {});
        let enriched = houseList.map(h => ({
          ...h,
          series_name: seriesDict[h.series_id] || `#${h.series_id}`,
        }));
      /* 1️⃣ читаем оптимистичные добавления за последний час */
      const now = Date.now();
      let optimistic = [];
      try {
        optimistic = JSON.parse(localStorage.getItem(STORAGE_ADDED_KEY) || '[]')
                       .filter(h => now - h.timestamp < ONE_HOUR);
      } catch {/* ignore */}

      /* 2️⃣ убираем дубликаты (значит объект уже попал в БД) */
      const stillMissing = optimistic.filter(
        h => !enriched.some(db => db.id === h.id)
      );

      /* 3️⃣ обновляем кеш, если что-то устарело */
      localStorage.setItem(STORAGE_ADDED_KEY, JSON.stringify(stillMissing));

      /* 4️⃣ склеиваем и сохраняем */
      enriched = [...enriched, ...stillMissing];
      setHouses(enriched);
      })
      .catch(err => console.error('[DBHouseTable] Ошибка загрузки домов:', err));

    // 1.b. Считываем из localStorage удалённые дома
    try {
      const raw = localStorage.getItem(STORAGE_HIDE_KEY);
      let stored = raw ? JSON.parse(raw) : [];
      const now = Date.now();

      // Оставляем только те записи, у которых метка времени < 1 часа назад
      const valid = stored.filter(item => now - item.timestamp < ONE_HOUR);

      // Обновляем hideIds и localStorage
      const idsToHide = valid.map(item => item.id);
      setHideIds(idsToHide);
      localStorage.setItem(STORAGE_HIDE_KEY, JSON.stringify(valid));
      window.dispatchEvent(new Event('deletedHousesChange'));   // ⚡︎ оповещаем карту
    } catch (e) {
      console.error('[DBHouseTable] Ошибка чтения/очистки localStorage:', e);
      setHideIds([]);
    }
  }, []);

  // ────────────────────────────────────────────────────────────────────────────────
  // 2) Если из HouseDetails пришёл deletedId, добавляем его в localStorage и hideIds
  useEffect(() => {
    const deletedId = location.state?.deletedId;
    if (deletedId != null) {
      console.log('[DBHouseTable] Получен deletedId =', deletedId, '— помечаем для скрытия.');
      const now = Date.now();

      // Читаем текущий массив из localStorage
      let stored = [];
      try {
        const raw = localStorage.getItem(STORAGE_HIDE_KEY);
        stored = raw ? JSON.parse(raw) : [];
      } catch {
        stored = [];
      }

      // Если ещё нет такого id в списке, добавляем
      if (!stored.some(item => item.id === deletedId)) {
        stored.push({ id: deletedId, timestamp: now });
      }

      // Удаляем устаревшие (>1 час) записи
      const valid = stored.filter(item => now - item.timestamp < ONE_HOUR);

      // Сохраняем обратно
      localStorage.setItem(STORAGE_HIDE_KEY, JSON.stringify(valid));
      setHideIds(valid.map(item => item.id));

      // Очищаем state, чтобы не повторяться при роутинге/обновлении
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 3) Фильтрация по query (по серии и адресу)
  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return houses;
    return houses.filter(
      h =>
        h.series_name.toLowerCase().includes(q) ||
        h.address.toLowerCase().includes(q)
    );
  }, [houses, query]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 4) Сортировка по выбранному ключу
  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    const arr = [...filtered].sort((a, b) => {
      let aV = a[sortConfig.key],
        bV = b[sortConfig.key];
      if (
        ['floors', 'count_appartaments', 'construction_year'].includes(
          sortConfig.key
        )
      ) {
        aV = Number(aV) || 0;
        bV = Number(bV) || 0;
      } else {
        aV = (aV || '').toString().toLowerCase();
        bV = (bV || '').toString().toLowerCase();
      }
      if (aV < bV) return -1;
      if (aV > bV) return 1;
      return 0;
    });
    return sortConfig.direction === 'asc' ? arr : arr.reverse();
  }, [filtered, sortConfig]);

  const handleSort = key => {
    setSortConfig(prev => {
      const next =
        prev.key === key
          ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
          : { key, direction: 'asc' };
      return next;
    });
  };

  // Сохраняем состояние сортировки в sessionStorage при его изменении
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_SORT_KEY, JSON.stringify(sortConfig));
    } catch {}
  }, [sortConfig]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 5) Применяем скрытие: убираем из sorted все, чьи id есть в hideIds
  const visible = useMemo(() => {
    if (hideIds.length === 0) {
      return sorted;
    }
    return sorted.filter(h => !hideIds.includes(h.id));
  }, [sorted, hideIds]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 6) Восстановление прокрутки таблицы (useLayoutEffect для корректного позиционирования до отрисовки)
  useLayoutEffect(() => {
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
      if (savedScroll && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = Number(savedScroll);
      }
    } catch {}
  }, [visible]);

  // Обработчик прокрутки: сохраняем текущую позицию
  const handleScroll = () => {
    if (tableWrapperRef.current) {
      try {
        sessionStorage.setItem(STORAGE_SCROLL_KEY, tableWrapperRef.current.scrollTop.toString());
      } catch {}
    }
  };

  // Обработчик клика по строке: сохраняем прокрутку перед навигацией
  const handleRowClick = id => {
    if (tableWrapperRef.current) {
      try {
        sessionStorage.setItem(STORAGE_SCROLL_KEY, tableWrapperRef.current.scrollTop.toString());
      } catch {}
    }
    onRowSelect(id);
  };

  return (
    <div className="table-container">
      <div
        className="table-wrapper"
        ref={tableWrapperRef}
        onScroll={handleScroll}
      >
        <table className="custom-table">
          <colgroup>
            <col style={{ width: '15%' }} />
            <col style={{ width: '33%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>
          <thead>
            <tr>
              <th
                className="sortable-header date-col"
                onClick={() => handleSort('series_name')}
              >
                Серия
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('address')}
              >
                Адрес дома
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('floors')}
              >
                Этажность
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('count_appartaments')}
              >
                Квартиры
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('construction_year')}
              >
                Год постройки
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map(h => {
              const series = h.series_name?.trim() ? h.series_name : '-';
              const address = h.address?.trim() ? h.address : '-';
              const floorsN = Number(h.floors) || 0;
              const floors =
                floorsN > 0
                  ? `${floorsN} ${declOfNum(floorsN, ['этаж', 'этажа', 'этажей'])}`
                  : '-';
              const aptN = parseInt(h.count_appartaments, 10) || 0;
              const apartments =
                aptN > 0
                  ? `${aptN} ${declOfNum(aptN, ['квартира', 'квартиры', 'квартир'])}`
                  : '-';
              const yearNum = parseInt(h.construction_year, 10) || 0;
              const year = yearNum > 0 ? `${yearNum} г.` : '-';

              return (
                <tr
                  key={h.id}
                  className={`clickable-row${
                    h.id === selectedHouseId ? ' selected-row' : ''
                  }`}
                  onClick={() => handleRowClick(h.id)}
                >
                  <td className="date-col">{series}</td>
                  <td>{address}</td>
                  <td>{floors}</td>
                  <td>{apartments}</td>
                  <td>{year}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DBHouseTable;
